/** @format */

import '@/shared/lib/zod-csp-config' // must be first — runs before any z.object() is evaluated
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'

import { ToastProvider } from '@/shared/ui/organisms/toast'
import 'react-phone-number-input/style.css'
import './i18n'

import { initLanguageSync } from './shared/i18n/language-sync'
import { useLanguageStore } from '@/entities/language/model'
import { Toast } from '@/shared/ui/organisms/toast'
import { queryClient } from '@/shared/api/queryClient'
import { setupCSPViolationListener } from './utils/securityLogger'
import { StableNavigateContextProvider } from '@/shared/lib/stable-navigate'
import { ScrollToTop } from '@/shared/lib/scroll-to-top'
import { AuthBootstrap } from '@/app/bootstrap'
import { LogoPreLoader } from '@/shared/ui/icons'
import { NetworkProvider } from '@/app/network-provider'
import { OfflineModalController } from '@/app/network-provider/offline-modal-controller'

// Listen for CSP violations from the start
setupCSPViolationListener()

// Handle chunk load errors in 4 scenarios:
// 1. After deployment — stale index.html references old chunk hashes
// 2. Dev server restart — user restarts yarn start, old chunks gone
// 3. System restart — Chrome restores tab, dev server is a new process
// 4. Transient network drop — VPN reconnect, antivirus interrupt, WiFi switch
//
// Uses a TIMESTAMP-based debounce instead of a persistent flag so that:
// - Rapid successive failures don't infinite-loop (5s debounce)
// - Old flags from yesterday's session don't block today's reload
// - Chrome "continue where you left off" doesn't trap user in error state
const CHUNK_RELOAD_KEY = 'chunk-reload-last-at'
const RELOAD_DEBOUNCE_MS = 5000

/**
 * Detect whether an error is a dynamic import failure that should trigger reload.
 * Covers both Vite's internal preload error and the native TypeError from
 * `import()` when the chunk fetch fails.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    (message.includes('Loading chunk') && message.includes('failed'))
  )
}

/**
 * Show a minimal full-screen overlay before reload (pure DOM, no React,
 * since React may be in a broken state). Built with createElement to avoid
 * innerHTML security warnings.
 */
function showReloadOverlay() {
  try {
    if (document.querySelector('[data-chunk-reload-overlay]')) return

    // Spinner keyframes
    const styleEl = document.createElement('style')
    styleEl.textContent = '@keyframes chunk-reload-spin { to { transform: rotate(360deg) } }'
    document.head.appendChild(styleEl)

    const overlay = document.createElement('div')
    overlay.setAttribute('data-chunk-reload-overlay', '')
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'background:rgba(255,255,255,0.92)',
      'backdrop-filter:blur(4px)',
      'z-index:2147483647',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'font-family:Roboto, system-ui, sans-serif',
      'color:#1A1C3B',
      'font-size:14px',
    ].join(';')

    const box = document.createElement('div')
    box.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:12px'

    const spinner = document.createElement('div')
    spinner.style.cssText = [
      'width:32px',
      'height:32px',
      'border:3px solid #E6E8F0',
      'border-top-color:#1890FF',
      'border-radius:50%',
      'animation:chunk-reload-spin 0.8s linear infinite',
    ].join(';')

    const label = document.createElement('div')
    label.textContent = 'Yangilanmoqda…'

    box.appendChild(spinner)
    box.appendChild(label)
    overlay.appendChild(box)
    document.body.appendChild(overlay)
  } catch {
    // ignore — reload proceeds regardless
  }
}

/**
 * Smoothly reload the page with debounce protection.
 */
function attemptChunkReload(source: string, error?: unknown): boolean {
  const now = Date.now()
  const lastAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)

  if (now - lastAt <= RELOAD_DEBOUNCE_MS) {
    if (import.meta.env.DEV) {
      console.error(`[chunk-reload] ${source}: within debounce window — not reloading`, error)
    }
    return false
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now))
  if (import.meta.env.DEV) {
    console.warn(`[chunk-reload] ${source}: reloading…`, error)
  }

  showReloadOverlay()
  // Small delay so the overlay has time to render before navigation
  window.setTimeout(() => window.location.reload(), 50)
  return true
}

// 1. Vite's internal preload error event (fires before the promise rejection
//    bubbles to React). Preventing default stops Vite's own error from being
//    thrown upward.
window.addEventListener('vite:preloadError', (event) => {
  attemptChunkReload('vite:preloadError', event)
  event.preventDefault()
})

// 2. Global unhandled promise rejection — catches dynamic import failures that
//    bypass Vite's handler (e.g., when `import()` is called manually).
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    if (attemptChunkReload('unhandledrejection', event.reason)) {
      event.preventDefault()
    }
  }
})

// 3. Global error event — catches chunk load errors that manifest as
//    synchronous script errors (very rare but covers all bases).
window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
    if (attemptChunkReload('window.error', event.error ?? event.message)) {
      event.preventDefault()
    }
  }
})

// Sync Zustand language store → i18next
initLanguageSync()

// SecurityDashboard removed — was dev-only, file deleted during legacy cleanup

// QueryClient construction lives in src/shared/api/queryClient.ts so it can
// be imported by non-React modules (axios interceptors, cross-tab logout
// listener) without prop-drilling. See ADR-0009.

useLanguageStore.subscribe((state, prev) => {
  if (state.lang !== prev.lang) queryClient.invalidateQueries()
})

// Register Service Worker for Firebase Cloud Messaging
const registerServiceWorker = async () => {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window) {
    try {
      // Prepare Firebase config for service worker
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
      }

      // Encode config to pass to service worker
      const serializedConfig = btoa(JSON.stringify(firebaseConfig))

      // Register service worker with config
      const registration = await navigator.serviceWorker.register(
        `/firebase-messaging-sw.js?firebaseConfig=${serializedConfig}`,
        { scope: '/' },
      )

      return registration
    } catch (error) {
      return null
    }
  }
  return null
}

// Register service worker when window loads
if (typeof window !== 'undefined') {
  window.addEventListener('load', registerServiceWorker)
}

const root = createRoot(document.getElementById('root')!, {
  onCaughtError: (error, errorInfo) => {
    // 4. Chunk load errors caught by React ErrorBoundary — reload smoothly
    //    instead of showing the fallback UI. Covers the case where the
    //    dynamic import TypeError bubbles up through React before Vite's
    //    vite:preloadError event listener can handle it.
    if (isChunkLoadError(error)) {
      attemptChunkReload('onCaughtError', error)
      return
    }
    // Only log to console in development — no noisy toast with stack trace
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught:', error, errorInfo)
    }
    // Log to error tracking service if available
    if (window.logError) {
      window.logError(error, errorInfo)
    }
  },
  onUncaughtError: (error, errorInfo) => {
    // Chunk load errors uncaught by any boundary — reload smoothly
    if (isChunkLoadError(error)) {
      attemptChunkReload('onUncaughtError', error)
      return
    }

    const errorMessage = `Uncaught error: ${error instanceof Error ? error.message : String(error)}${
      errorInfo?.componentStack ? ` - ${String(errorInfo.componentStack)}` : ''
    }`

    // Only log to console in development
    if (import.meta.env.DEV) {
      Toast({
        type: 'error',
        message: errorMessage,
      })
    }
    // Show user-friendly notification
    Toast({
      type: 'error',
      message: 'An unexpected error occurred. Please refresh the page.',
    })
    // Log to error tracking service if available
    if (window.logError) {
      window.logError(error, errorInfo)
    }
  },
  onRecoverableError: (error, errorInfo) => {
    // Only log to console in development
    if (import.meta.env.DEV) {
      const errorMessage = `Recovered from error: ${error instanceof Error ? error.message : String(error)}${
        errorInfo ? ` - ${JSON.stringify(errorInfo)}` : ''
      }`
      Toast({
        type: 'error',
        message: errorMessage,
      })
    }
  },
})

// MSW (Mock Service Worker) is opt-in via VITE_ENABLE_MSW=true to avoid
// (a) duplicate-row noise in DevTools (every request shows the SW + the
// passthrough fetch) and (b) the Firebase FCM SW being blocked from scope
// '/' in DEV when MSW wins the registration race. Default OFF — turn ON
// in `.env.development.local` only when working on the accounting feature
// (backend /v1/accounting/* not shipped — see ACCOUNTING_GAPS.md). The
// dynamic import gates both DEV + flag so prod bundles tree-shake every
// MSW dependency out. `onUnhandledRequest: 'bypass'` lets non-accounting
// requests fall through to the real backend.
if (import.meta.env.DEV) {
  try {
    if (import.meta.env.VITE_ENABLE_MSW === 'true') {
      const { worker } = await import('./mocks/browser')
      await worker.start({
        onUnhandledRequest: 'bypass',
        quiet: true,
        serviceWorker: { url: '/mockServiceWorker.js' },
      })
    } else if ('serviceWorker' in navigator) {
      // Flag was on in a prior session — unregister any lingering MSW SW so
      // requests stop being intercepted. Check all 3 lifecycle slots
      // (installing/waiting/active) to handle hot-reload race. Firebase FCM
      // SW (different scriptURL) is left alone.
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(
        regs
          .filter((r) => {
            const sw = r.active ?? r.waiting ?? r.installing
            return sw?.scriptURL.endsWith('/mockServiceWorker.js') ?? false
          })
          .map((r) => r.unregister()),
      )
    }
  } catch (err) {
    // Don't let MSW setup block React mount. Browsers with SWs disabled
    // (privacy mode, corporate policy) reject getRegistrations() — soft-fail
    // so root.render() still runs.
    console.warn('[MSW gate] failed — continuing to render', err)
  }
}

root.render(
  <StrictMode>
    <BrowserRouter>
      <ScrollToTop />
      <StableNavigateContextProvider>
        <QueryClientProvider client={queryClient}>
          <AuthBootstrap>
            <ToastProvider>
              <NetworkProvider>
                <Suspense
                  fallback={
                    <div className='flex items-center justify-center w-full h-screen'>
                      <LogoPreLoader size='large' />
                    </div>
                  }
                >
                  <App />
                </Suspense>
                <OfflineModalController />
              </NetworkProvider>
            </ToastProvider>
          </AuthBootstrap>
        </QueryClientProvider>
      </StableNavigateContextProvider>
    </BrowserRouter>
  </StrictMode>,
)
